import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useContainerDimensions } from '@/hooks/useContainerDimensions';

interface StaticMapProps {
  latitude: number;
  longitude: number;
  zoom?: number;
  width?: number;
  height?: number;
  className?: string;
  showPin?: boolean;
}

export function StaticMap({
  latitude,
  longitude,
  zoom = 15,
  width,
  height,
  className,
  showPin = true,
}: StaticMapProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  // Use container dimensions if width/height not provided (responsive mode)
  const { ref: containerRef, dimensions } = useContainerDimensions({
    defaultWidth: width || 300,
    defaultHeight: height || 200,
  });

  // Use provided dimensions or responsive dimensions
  const mapWidth = width || dimensions.width;
  const mapHeight = height || dimensions.height;

  // Use standard 256px tiles for better compatibility
  const tileSize = 256;
  const n = Math.pow(2, zoom);

  // Convert lat/lng to pixel coordinates at this zoom level
  const worldPixelX = ((longitude + 180) / 360) * n * tileSize;
  const worldPixelY =
    ((1 -
      Math.log(
        Math.tan((latitude * Math.PI) / 180) +
          1 / Math.cos((latitude * Math.PI) / 180),
      ) /
        Math.PI) /
      2) *
    n *
    tileSize;

  // Calculate which tiles we need to cover the container
  const containerCenterX = mapWidth / 2;
  const containerCenterY = mapHeight / 2;

  // Calculate the pixel bounds we need to cover
  const minPixelX = worldPixelX - containerCenterX;
  const maxPixelX = worldPixelX + containerCenterX;
  const minPixelY = worldPixelY - containerCenterY;
  const maxPixelY = worldPixelY + containerCenterY;

  // Convert pixel bounds to tile bounds
  const minTileX = Math.floor(minPixelX / tileSize);
  const maxTileX = Math.floor(maxPixelX / tileSize);
  const minTileY = Math.floor(minPixelY / tileSize);
  const maxTileY = Math.floor(maxPixelY / tileSize);

  // Calculate offset to center the coordinate
  const offsetX = containerCenterX - (worldPixelX - minTileX * tileSize);
  const offsetY = containerCenterY - (worldPixelY - minTileY * tileSize);

  // Generate tiles
  const tiles = [];
  for (let tileY = minTileY; tileY <= maxTileY; tileY++) {
    for (let tileX = minTileX; tileX <= maxTileX; tileX++) {
      // Only add valid tiles
      if (tileX >= 0 && tileX < n && tileY >= 0 && tileY < n) {
        tiles.push({
          x: tileX,
          y: tileY,
          pixelX: (tileX - minTileX) * tileSize,
          pixelY: (tileY - minTileY) * tileSize,
          url: `https://tile.openstreetmap.org/${zoom}/${tileX}/${tileY}.png`,
        });
      }
    }
  }

  if (imageError) {
    return (
      <div
        ref={containerRef}
        className={cn(
          `bg-muted text-muted-foreground flex items-center justify-center rounded border
          text-sm`,
          className,
        )}
        style={{ width: width || '100%', height: height || '100%' }}
      >
        Map unavailable
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'bg-muted relative overflow-hidden rounded border',
        className,
      )}
      style={{ width: width || '100%', height: height || '100%' }}
    >
      {/* Tile container */}
      <div
        className="absolute"
        style={{
          left: offsetX,
          top: offsetY,
          width: (maxTileX - minTileX + 1) * tileSize,
          height: (maxTileY - minTileY + 1) * tileSize,
        }}
      >
        {tiles.map((tile) => (
          <img
            key={`${tile.x}-${tile.y}`}
            src={tile.url}
            alt=""
            className="absolute block"
            style={{
              left: tile.pixelX,
              top: tile.pixelY,
              width: tileSize,
              height: tileSize,
            }}
            onLoad={() => setImageLoading(false)}
            onError={() => setImageError(true)}
          />
        ))}
      </div>

      {/* Location pin */}
      {showPin && !imageError && (
        <div
          className="pointer-events-none absolute z-10 h-6 w-6 -translate-x-1/2 -translate-y-full"
          style={{
            left: containerCenterX,
            top: containerCenterY,
          }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            className="h-full w-full drop-shadow-md"
          >
            <path
              d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
              fill="#ef4444"
              stroke="#dc2626"
              strokeWidth="1"
            />
            <circle cx="12" cy="9" r="2.5" fill="white" />
          </svg>
        </div>
      )}

      {/* Loading overlay */}
      {imageLoading && !imageError && (
        <div className="bg-muted/50 absolute inset-0 flex items-center justify-center">
          <div className="border-primary h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      )}

      {/* Attribution (required by OpenStreetMap) */}
      <div className="absolute right-0 bottom-0 rounded-tl bg-black/70 px-1 py-0.5 text-xs text-white">
        <a
          href="https://www.openstreetmap.org/"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline"
        >
          © OSM
        </a>
      </div>
    </div>
  );
}
